import React, { useState, useEffect, useCallback } from "react";
import { Heart, X, MapPin, ShieldCheck, Baby, Sparkles, Crown, Filter, ChevronLeft, AlertTriangle, Utensils } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/App";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import { motion, AnimatePresence } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Discover = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [families, setFamilies] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [remainingLikes, setRemainingLikes] = useState(3);
  const [cityFilter, setCityFilter] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [direction, setDirection] = useState(0);

  const fetchFamilies = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (cityFilter) params.append('city', cityFilter);
      
      const response = await axios.get(`${API}/discover?${params.toString()}`);
      setFamilies(response.data.families || []);
      setRemainingLikes(response.data.remaining_likes);
      setCurrentIndex(0);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("Please complete your profile first");
        navigate('/onboarding');
      } else {
        toast.error("Failed to load families");
      }
    } finally {
      setLoading(false);
    }
  }, [cityFilter, navigate]);

  useEffect(() => {
    fetchFamilies();
  }, [fetchFamilies]);

  const handleAction = async (action) => {
    if (currentIndex >= families.length) return;
    
    const currentFamily = families[currentIndex];
    setDirection(action === 'like' ? 1 : -1);
    
    try {
      const response = await axios.post(`${API}/match/${currentFamily.user_id}?action=${action}`);
      
      if (response.data.is_mutual) {
        toast.success(
          <div className="flex items-center gap-3">
            <Heart className="w-6 h-6 text-pink-500" fill="#F472B6" />
            <span>It's a match! Start chatting now.</span>
          </div>
        );
      }
      
      if (action === 'like' && !user?.is_premium) {
        setRemainingLikes(prev => Math.max(0, prev - 1));
      }
      
      setTimeout(() => {
        setCurrentIndex(prev => prev + 1);
        setDirection(0);
      }, 300);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(
          <div>
            <p className="font-semibold">Daily limit reached!</p>
            <p className="text-sm">Upgrade to premium for unlimited matches</p>
          </div>
        );
      } else {
        toast.error(error.response?.data?.detail || "Action failed");
      }
    }
  };

  const currentFamily = families[currentIndex];

  return (
    <div className="min-h-screen bg-[#FFF9F5] pb-24" data-testid="discover-page">
      {/* Decorative blobs */}
      <div className="blob blob-pink w-64 h-64 -top-32 right-0 fixed opacity-30" />
      <div className="blob blob-purple w-48 h-48 bottom-32 -left-24 fixed opacity-30" />

      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-slate-100">
        <div className="max-w-lg mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="text-slate-600 hover:text-slate-800">
              <ChevronLeft className="w-6 h-6" />
            </Link>
            <h1 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Discover Families
            </h1>
            <button
              onClick={() => setShowFilter(!showFilter)}
              className="p-2 hover:bg-slate-100 rounded-full"
              data-testid="filter-btn"
            >
              <Filter className="w-5 h-5 text-slate-600" />
            </button>
          </div>
          
          {/* Filter */}
          {showFilter && (
            <div className="mt-4 animate-fade-in">
              <div className="flex gap-2">
                <Input
                  placeholder="Filter by city..."
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  className="h-10 rounded-xl"
                  data-testid="city-filter-input"
                />
                <Button
                  onClick={fetchFamilies}
                  className="bg-teal-500 hover:bg-teal-600 text-white rounded-xl"
                  data-testid="apply-filter-btn"
                >
                  Apply
                </Button>
              </div>
            </div>
          )}

          {/* Remaining likes for free users */}
          {!user?.is_premium && remainingLikes >= 0 && (
            <div className="mt-3 flex items-center justify-center gap-2 text-sm">
              <span className="text-slate-500">Likes remaining today:</span>
              <div className="flex gap-1">
                {[0, 1, 2].map((i) => (
                  <Heart
                    key={i}
                    className={`w-5 h-5 ${i < remainingLikes ? 'text-pink-500' : 'text-slate-300'}`}
                    fill={i < remainingLikes ? '#F472B6' : 'transparent'}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-lg mx-auto px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="w-12 h-12 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : families.length === 0 || currentIndex >= families.length ? (
          <div className="text-center py-20">
            <div className="w-24 h-24 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-6">
              <Heart className="w-12 h-12 text-slate-400" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              {families.length === 0 ? "No families found" : "All caught up!"}
            </h2>
            <p className="text-slate-500 mb-6">
              {families.length === 0 
                ? "Try adjusting your filters or check back later" 
                : "Check back later for new families in your area"}
            </p>
            <Button
              onClick={fetchFamilies}
              className="bg-teal-500 hover:bg-teal-600 text-white rounded-full"
              data-testid="refresh-btn"
            >
              Refresh
            </Button>
          </div>
        ) : (
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: direction * 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: direction * -100 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-3xl shadow-lg overflow-hidden"
                data-testid="family-card"
              >
                {/* Photo */}
                <div className="relative h-80 bg-gradient-to-br from-sky-400 to-purple-400">
                  {currentFamily.is_blurred ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center text-white">
                        <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4 text-5xl font-bold">
                          {currentFamily.parent_name?.[0]}
                        </div>
                        <p className="font-medium">Photo hidden</p>
                        <Link to="/subscription" className="text-sm opacity-80 underline">
                          Upgrade to see
                        </Link>
                      </div>
                    </div>
                  ) : currentFamily.photo_url ? (
                    <img
                      src={currentFamily.photo_url}
                      alt={currentFamily.parent_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-32 h-32 rounded-full bg-white/20 flex items-center justify-center text-white text-6xl font-bold">
                        {currentFamily.parent_name?.[0]}
                      </div>
                    </div>
                  )}
                  
                  {/* Verified badge */}
                  {currentFamily.is_verified && (
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur rounded-full px-3 py-1 flex items-center gap-1">
                      <ShieldCheck className="w-4 h-4 text-teal-500" />
                      <span className="text-sm font-medium text-slate-700">Verified</span>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>
                        {currentFamily.parent_name}
                      </h2>
                      <div className="flex items-center gap-2 text-slate-500">
                        <MapPin className="w-4 h-4" />
                        <span>{currentFamily.location_city || "Nearby"}</span>
                        {/* Near Me Distance Badge */}
                        {currentFamily.distance_miles !== undefined && (
                          <span className="inline-flex items-center gap-1 bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-2.5 py-0.5 rounded-full text-xs font-semibold">
                            <MapPin className="w-3 h-3" />
                            {currentFamily.distance_miles < 1 
                              ? "< 1 mile" 
                              : `${currentFamily.distance_miles} miles`}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Bio */}
                  {currentFamily.bio && (
                    <p className="text-slate-600 mb-4">{currentFamily.bio}</p>
                  )}

                  {/* Children */}
                  {currentFamily.children?.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                        <Baby className="w-4 h-4" />
                        <span>Kids</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {currentFamily.children.map((child, i) => (
                          <span key={i} className="bg-pink-100 text-pink-700 px-3 py-1 rounded-full text-sm">
                            {child.name}, {child.age}y
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Interests */}
                  {currentFamily.interests?.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                        <Sparkles className="w-4 h-4" />
                        <span>Interests</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {currentFamily.interests.map((interest, i) => (
                          <span key={i} className="bg-teal-100 text-sky-700 px-3 py-1 rounded-full text-sm">
                            {interest}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Allergens - Important safety info */}
                  {currentFamily.allergens?.length > 0 && (
                    <div className="mb-4 p-3 bg-amber-50 rounded-xl border border-amber-200">
                      <div className="flex items-center gap-2 text-sm text-amber-700 mb-2">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="font-medium">Allergens - Please don't bring:</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {currentFamily.allergens.map((allergen, i) => (
                          <span key={i} className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                            {allergen}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Dietary Preferences */}
                  {currentFamily.dietary_preferences?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                        <Utensils className="w-4 h-4" />
                        <span>Dietary Preferences</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {currentFamily.dietary_preferences.map((pref, i) => (
                          <span key={i} className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">
                            {pref}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Action Buttons */}
            <div className="flex justify-center gap-6 mt-6">
              <button
                onClick={() => handleAction('pass')}
                className="w-16 h-16 rounded-full bg-white shadow-lg flex items-center justify-center hover:scale-110 transition-transform active:scale-95"
                data-testid="pass-btn"
              >
                <X className="w-8 h-8 text-slate-400" />
              </button>
              <button
                onClick={() => handleAction('like')}
                disabled={!user?.is_premium && remainingLikes <= 0}
                className={`w-20 h-20 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform active:scale-95 ${
                  !user?.is_premium && remainingLikes <= 0
                    ? 'bg-slate-200 cursor-not-allowed'
                    : 'bg-gradient-to-br from-pink-400 to-rose-500'
                }`}
                data-testid="like-btn"
              >
                <Heart className="w-10 h-10 text-white" fill="white" />
              </button>
            </div>

            {/* Premium CTA */}
            {!user?.is_premium && remainingLikes <= 0 && (
              <Link to="/subscription">
                <div className="mt-6 p-4 premium-gradient rounded-2xl text-white text-center" data-testid="upgrade-cta">
                  <Crown className="w-6 h-6 mx-auto mb-2" />
                  <p className="font-semibold">Upgrade for unlimited likes!</p>
                </div>
              </Link>
            )}
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
};

export default Discover;